import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NativeCheckbox } from '@/components/ui/NativeCheckbox';
import { toast } from 'sonner';
import { api } from '@/App';
import Navbar from '@/components/Navbar';
import { COUNTRIES } from '@/data/countries';
import { usePhotoPreview } from '@/components/PhotoPreview';
import { StandingMovement } from '@/components/StandingMovement';

// Helper function to get full image URL
const getImageUrl = (photoUrl) => {
  if (!photoUrl) return null;
  // If it's a base64 data URL, return as is
  if (photoUrl.startsWith('data:image/')) {
    return photoUrl;
  }
  // If already absolute URL, return as is
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
    return photoUrl;
  }
  // If relative URL, prepend backend URL
  const backendUrl = window.location.origin || '';
  return `${backendUrl}${photoUrl}`;
};

const experienceLevels = [
  'Beginner',
  'Intermediate (1-3 years)',
  'Upper Intermediate (4-6 years)',
  'Advanced (7+ years)',
  'Professional (played in local or national leagues)'
];

const positions = [
  'Libero',
  'Outside Hitter',
  'Middle Blocker',
  'Opposite Hitter',
  'Setter'
];

const genderOptions = [
  'Male',
  'Female',
  'Non-Binary/Other',
  'Prefer not to say'
];

const levels = [
  '6th class',
  '5th class',
  '4th class',
  '3rd class',
  '2nd class',
  '1st class',
  'Promotion class',
  '2nd division',
  '1st division',
  'Other'
];

export default function AdminUsers({ user, onLogout }) {
  const previewPhoto = usePhotoPreview();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'pending'
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStanding, setUserStanding] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [createPhoto, setCreatePhoto] = useState(null);
  const [createPhotoPreview, setCreatePhotoPreview] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [createFormData, setCreateFormData] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    email: '',
    password: '',
    date_of_birth: '',
    phone: '',
    gender: '',
    rating: 0,
    volleyball_experience: '',
    position_1st: '',
    position_2nd: '',
    position_3rd: '',
    current_level: '',
    current_level_other: '',
    highest_level: '',
    highest_level_other: '',
    where_hear_about_us: '',
    club_experience: false,
    club_names: '',
    current_club: '',
    previous_clubs: '',
    country_of_origin: '',
    current_country: '',
    current_city: '',
    gdpr_consent: false
  });

  useEffect(() => {
    loadUsers();
  }, []); // Load once on mount

  // Fetch the selected user's community standing whenever the details dialog opens
  useEffect(() => {
    if (!showEditDialog || isEditing || !selectedUser?.id) {
      setUserStanding(null);
      return;
    }
    let cancelled = false;
    api.get(`/users/${selectedUser.id}/standing`)
      .then((res) => { if (!cancelled) setUserStanding(res.data); })
      .catch(() => { if (!cancelled) setUserStanding(null); });
    return () => { cancelled = true; };
  }, [showEditDialog, isEditing, selectedUser?.id]);

  // Deep-link: open a specific user's details when arriving via ?userId=...
  // (used by the "Review & Approve This User" button in the admin new-user email)
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    const deepUserId = searchParams.get('userId');
    if (!deepUserId || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true; // one-shot guard (also avoids StrictMode double-run)
    (async () => {
      try {
        const res = await api.get(`/users/${deepUserId}`);
        const u = res.data;
        if (!u.is_approved) setActiveTab('pending');
        setSelectedUser(u);
        setShowEditDialog(true);
      } catch (e) {
        toast.error('Could not open that user — they may have been removed.');
      } finally {
        // Clear the param so the dialog doesn't reopen on later renders
        searchParams.delete('userId');
        setSearchParams(searchParams, { replace: true });
      }
    })();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        loadUsers();
      }
    }, 300); // Wait 300ms after user stops typing
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const loadUsers = useCallback(async () => {
    try {
      // Load all users with search
      const response = await api.get(`/users?search=${encodeURIComponent(searchQuery)}`);
      setUsers(response.data);
      
      // Load pending users
      const pendingResponse = await api.get('/users/pending');
      setPendingUsers(pendingResponse.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const handleToggleParticipation = async () => {
    const newVal = selectedUser.rating_participation_enabled === false;
    try {
      await api.put(`/admin/users/${selectedUser.id}/rating-participation`, { enabled: newVal });
      setSelectedUser({ ...selectedUser, rating_participation_enabled: newVal });
      toast.success(`Rating participation ${newVal ? 'enabled' : 'disabled'} for ${selectedUser.first_name}`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rating participation');
    }
  };

  const handleSaveUser = async () => {
    try {
      await api.put(`/users/${selectedUser.id}`, editFormData);
      
      // Upload photo if provided (use admin endpoint to update target user)
      if (editPhoto) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', editPhoto);
          await api.post(`/admin/users/${selectedUser.id}/upload-photo`, photoFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          toast.warning('User updated but photo upload failed');
        }
      }
      
      toast.success('User updated successfully');
      setIsEditing(false);
      setShowEditDialog(false);
      setEditPhoto(null);
      setEditPhotoPreview(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleEditClick = () => {
    setEditFormData({
      first_name: selectedUser.first_name,
      last_name: selectedUser.last_name,
      preferred_name: selectedUser.preferred_name || '',
      email: selectedUser.email,
      phone: selectedUser.phone || '',
      date_of_birth: selectedUser.date_of_birth || '',
      volleyball_experience: selectedUser.volleyball_experience,
      club_experience: selectedUser.club_experience,
      club_names: selectedUser.club_names || '',
      current_club: selectedUser.current_club ?? (selectedUser.club_names || ''),
      previous_clubs: selectedUser.previous_clubs || '',
      preferred_positions: selectedUser.preferred_positions || [],
      // New enhanced fields
      gender: selectedUser.gender || '',
      rating: selectedUser.rating || 0,
      position_1st: selectedUser.position_1st || '',
      position_2nd: selectedUser.position_2nd || '',
      position_3rd: selectedUser.position_3rd || '',
      current_level: selectedUser.current_level || '',
      current_level_other: selectedUser.current_level_other || '',
      highest_level: selectedUser.highest_level || '',
      highest_level_other: selectedUser.highest_level_other || '',
      where_hear_about_us: selectedUser.where_hear_about_us || '',
      gdpr_consent: selectedUser.gdpr_consent || false,
      country_of_origin: selectedUser.country_of_origin || '',
      current_country: selectedUser.current_country || '',
      current_city: selectedUser.current_city || '',
      is_admin: selectedUser.is_admin,
      is_approved: selectedUser.is_approved,
      is_enabled: selectedUser.is_enabled
    });
    setIsEditing(true);
  };

  const togglePosition = (position) => {
    setEditFormData(prev => ({
      ...prev,
      preferred_positions: prev.preferred_positions.includes(position)
        ? prev.preferred_positions.filter(p => p !== position)
        : [...prev.preferred_positions, position]
    }));
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${userId}`);
        toast.success('User deleted successfully');
        loadUsers();
      } catch (error) {
        toast.error('Failed to delete user');
      }
    }
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      await api.put(`/users/${userId}`, { is_admin: !isAdmin });
      toast.success(`User ${!isAdmin ? 'promoted to' : 'removed from'} admin`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.post(`/users/${userId}/approve`);
      toast.success('User approved successfully!');
      loadUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    if (window.confirm('Are you sure you want to reject and remove this user?')) {
      try {
        await api.post(`/users/${userId}/reject`);
        toast.success('User rejected and removed');
        loadUsers();
      } catch (error) {
        toast.error('Failed to reject user');
      }
    }
  };

  const handleEnableUser = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/enable`);
      toast.success('User enabled successfully');
      loadUsers();
    } catch (error) {
      console.error('Enable user error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to enable user';
      toast.error(errorMsg);
    }
  };

  const handleDisableUser = async (userId) => {
    if (window.confirm('Are you sure you want to disable this user?')) {
      try {
        const response = await api.post(`/users/${userId}/disable`);
        toast.success('User disabled successfully');
        loadUsers();
      } catch (error) {
        console.error('Disable user error:', error);
        const errorMsg = error.response?.data?.detail || 'Failed to disable user';
        toast.error(errorMsg);
      }
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Enter new password for this user (min 6 characters):');
    if (newPassword && newPassword.length >= 6) {
      try {
        await api.post(`/users/${userId}/reset-password`, { new_password: newPassword });
        toast.success('Password reset successfully! User has been notified via email.');
      } catch (error) {
        toast.error('Failed to reset password');
      }
    } else if (newPassword) {
      toast.error('Password must be at least 6 characters');
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await api.get('/admin/users/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nl_volleyball_users_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Users exported successfully!');
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!createFormData.volleyball_experience) {
      toast.error('Please select volleyball experience');
      return;
    }
    
    try {
      const response = await api.post('/users/create', createFormData);
      
      // Upload photo if provided (use admin endpoint targeting the new user)
      if (createPhoto && response.data.id) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', createPhoto);
          await api.post(`/admin/users/${response.data.id}/upload-photo`, photoFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
        }
      }
      
      toast.success('User created successfully!');
      setShowCreateDialog(false);
      setCreateFormData({
        first_name: '',
        last_name: '',
        preferred_name: '',
        email: '',
        password: '',
        date_of_birth: '',
        phone: '',
        gender: '',
        volleyball_experience: '',
        position_1st: '',
        position_2nd: '',
        position_3rd: '',
        current_level: '',
        current_level_other: '',
        highest_level: '',
        highest_level_other: '',
        where_hear_about_us: '',
        club_experience: false,
        club_names: '',
        country_of_origin: '',
        current_country: '',
        current_city: '',
        gdpr_consent: false
      });
      setCreatePhoto(null);
      setCreatePhotoPreview(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <div className="text-2xl font-semibold text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="mb-6 sm:mb-12 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">Manage Users</h1>
            <p className="text-base sm:text-xl text-gray-600">{users.length} total members</p>
          </div>
          <div className="flex gap-2 sm:gap-3 flex-wrap">
            <Button
              onClick={handleExportUsers}
              className="flex-1 sm:flex-none bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold px-4 sm:px-6 py-3 sm:py-6 rounded-full text-sm sm:text-base"
            >
              📥 <span className="hidden sm:inline">Export to Excel</span><span className="sm:hidden">Export</span>
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-user-btn"
                  className="flex-1 sm:flex-none bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-4 sm:px-6 py-3 sm:py-6 rounded-full text-sm sm:text-base"
                >
                  + <span className="hidden sm:inline">Create User</span><span className="sm:hidden">New</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4 pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      required
                      value={createFormData.first_name}
                      onChange={(e) => setCreateFormData({...createFormData, first_name: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      required
                      value={createFormData.last_name}
                      onChange={(e) => setCreateFormData({...createFormData, last_name: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="preferred_name">Preferred Name (Optional)</Label>
                  <Input
                    id="preferred_name"
                    value={createFormData.preferred_name}
                    onChange={(e) => setCreateFormData({...createFormData, preferred_name: e.target.value})}
                    className="mt-2"
                    placeholder="If different from first name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData({...createFormData, email: e.target.value})}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={createFormData.password}
                    onChange={(e) => setCreateFormData({...createFormData, password: e.target.value})}
                    className="mt-2"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      required
                      value={createFormData.date_of_birth}
                      onChange={(e) => setCreateFormData({...createFormData, date_of_birth: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      required
                      value={createFormData.phone}
                      onChange={(e) => setCreateFormData({...createFormData, phone: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="gender">Gender *</Label>
                  <Select
                    value={createFormData.gender}
                    onValueChange={(value) => setCreateFormData({...createFormData, gender: value})}
                  >
                    <SelectTrigger className="mt-2" id="gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      {genderOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Helps balance teams</p>
                </div>

                <div>
                  <Label htmlFor="create_profile_photo">Profile Photo (Optional)</Label>
                  <Input
                    id="create_profile_photo"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('File size must be less than 5MB');
                          e.target.value = '';
                          return;
                        }
                        setCreatePhoto(file);
                        setCreatePhotoPreview(URL.createObjectURL(file));
                      }
                    }}
                    className="mt-2"
                  />
                  {createPhotoPreview && (
                    <div className="mt-3 flex items-center gap-4">
                      <img src={createPhotoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-orange-500" />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCreatePhoto(null);
                          setCreatePhotoPreview(null);
                          document.getElementById('create_profile_photo').value = '';
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Upload jpg, png, or webp (max 5MB)</p>
                </div>

                <div>
                  <Label htmlFor="rating">Player Rating (0-10)</Label>
                  <Input
                    id="rating"
                    type="number"
                    min="0"
                    max="10"
                    step="0.5"
                    value={createFormData.rating}
                    onChange={(e) => setCreateFormData({...createFormData, rating: parseFloat(e.target.value) || 0})}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Set player's skill rating. Leave at 0 for no admin rating (community rating will be used).</p>
                </div>

                <div>
                  <Label htmlFor="volleyball_experience">Volleyball Experience *</Label>
                  <Select
                    value={createFormData.volleyball_experience}
                    onValueChange={(value) => setCreateFormData({...createFormData, volleyball_experience: value})}
                  >
                    <SelectTrigger className="mt-2" id="volleyball_experience">
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate (1-3 years)">Intermediate (1-3 years)</SelectItem>
                      <SelectItem value="Upper Intermediate (4-6 years)">Upper Intermediate (4-6 years)</SelectItem>
                      <SelectItem value="Advanced (7+ years)">Advanced (7+ years)</SelectItem>
                      <SelectItem value="Professional (played in local or national leagues)">Professional (played in local or national leagues)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="position_1st">1st Favorite Position *</Label>
                  <Select
                    value={createFormData.position_1st}
                    onValueChange={(value) => setCreateFormData({...createFormData, position_1st: value})}
                  >
                    <SelectTrigger className="mt-2" id="position_1st">
                      <SelectValue placeholder="Select 1st favorite" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map((pos) => (
                        <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="position_2nd">2nd Position (Optional)</Label>
                    <Select
                      value={createFormData.position_2nd}
                      onValueChange={(value) => setCreateFormData({...createFormData, position_2nd: value})}
                    >
                      <SelectTrigger className="mt-2" id="position_2nd">
                        <SelectValue placeholder="Select 2nd" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="position_3rd">3rd Position (Optional)</Label>
                    <Select
                      value={createFormData.position_3rd}
                      onValueChange={(value) => setCreateFormData({...createFormData, position_3rd: value})}
                    >
                      <SelectTrigger className="mt-2" id="position_3rd">
                        <SelectValue placeholder="Select 3rd" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Club Experience *</Label>
                  <div className="flex gap-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <NativeCheckbox
                        id="club-yes-create"
                        checked={createFormData.club_experience === true}
                        onChange={(checked) => setCreateFormData({...createFormData, club_experience: true})}
                      />
                      <label htmlFor="club-yes-create" className="text-sm font-medium cursor-pointer">Yes</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <NativeCheckbox
                        id="club-no-create"
                        checked={createFormData.club_experience === false}
                        onChange={(checked) => setCreateFormData({...createFormData, club_experience: false})}
                      />
                      <label htmlFor="club-no-create" className="text-sm font-medium cursor-pointer">No</label>
                    </div>
                  </div>
                </div>
                {createFormData.club_experience && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="create_current_club">Current Club</Label>
                      <Input
                        id="create_current_club"
                        value={createFormData.current_club}
                        onChange={(e) => setCreateFormData({...createFormData, current_club: e.target.value})}
                        className="mt-2"
                        placeholder="e.g., Amsterdam Volleyball Club"
                      />
                    </div>
                    <div>
                      <Label htmlFor="create_previous_clubs">Previous Clubs (optional)</Label>
                      <Input
                        id="create_previous_clubs"
                        value={createFormData.previous_clubs}
                        onChange={(e) => setCreateFormData({...createFormData, previous_clubs: e.target.value})}
                        className="mt-2"
                        placeholder="Rotterdam VC, Utrecht Smash"
                      />
                    </div>
                  </div>
                )}

                {/* Conditional Level Fields */}
                {createFormData.volleyball_experience && createFormData.volleyball_experience !== 'Beginner' && (
                  <>
                    <div>
                      <Label htmlFor="current_level">Current Level *</Label>
                      <Select
                        value={createFormData.current_level}
                        onValueChange={(value) => setCreateFormData({...createFormData, current_level: value})}
                      >
                        <SelectTrigger className="mt-2" id="current_level">
                          <SelectValue placeholder="Select current level" />
                        </SelectTrigger>
                        <SelectContent>
                          {levels.map((level) => (
                            <SelectItem key={level} value={level}>{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {createFormData.current_level === 'Other' && (
                      <div>
                        <Label htmlFor="current_level_other">Specify Current Level *</Label>
                        <Input
                          id="current_level_other"
                          value={createFormData.current_level_other}
                          onChange={(e) => setCreateFormData({...createFormData, current_level_other: e.target.value})}
                          className="mt-2"
                          placeholder="e.g., Regional league"
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="highest_level">Highest Level *</Label>
                      <Select
                        value={createFormData.highest_level}
                        onValueChange={(value) => setCreateFormData({...createFormData, highest_level: value})}
                      >
                        <SelectTrigger className="mt-2" id="highest_level">
                          <SelectValue placeholder="Select highest level" />
                        </SelectTrigger>
                        <SelectContent>
                          {levels.map((level) => (
                            <SelectItem key={level} value={level}>{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {createFormData.highest_level === 'Other' && (
                      <div>
                        <Label htmlFor="highest_level_other">Specify Highest Level *</Label>
                        <Input
                          id="highest_level_other"
                          value={createFormData.highest_level_other}
                          onChange={(e) => setCreateFormData({...createFormData, highest_level_other: e.target.value})}
                          className="mt-2"
                          placeholder="e.g., National team"
                        />
                      </div>
                    )}
                  </>
                )}

                <div>
                  <Label htmlFor="country_of_origin">Country of Origin</Label>
                  <Select
                    value={createFormData.country_of_origin}
                    onValueChange={(value) => setCreateFormData({...createFormData, country_of_origin: value})}
                  >
                    <SelectTrigger className="mt-2" id="country_of_origin">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="current_country">Current Country</Label>
                  <Select
                    value={createFormData.current_country}
                    onValueChange={(value) => setCreateFormData({...createFormData, current_country: value})}
                  >
                    <SelectTrigger className="mt-2" id="current_country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="current_city">Current City</Label>
                  <Input
                    id="current_city"
                    value={createFormData.current_city}
                    onChange={(e) => setCreateFormData({...createFormData, current_city: e.target.value})}
                    className="mt-2"
                    placeholder="e.g., Amsterdam"
                  />
                </div>

                <div>
                  <Label htmlFor="where_hear_about_us">Where did you hear about us? *</Label>
                  <Input
                    id="where_hear_about_us"
                    value={createFormData.where_hear_about_us}
                    onChange={(e) => setCreateFormData({...createFormData, where_hear_about_us: e.target.value})}
                    className="mt-2"
                    placeholder="e.g., Friend, Instagram..."
                  />
                </div>

                <div className="bg-blue-50 p-3 rounded">
                  <NativeCheckbox
                    id="gdpr-consent-create"
                    checked={createFormData.gdpr_consent}
                    onChange={(checked) => setCreateFormData({...createFormData, gdpr_consent: checked})}
                    label={<span className="text-sm">GDPR: Agree to data processing *</span>}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold py-6"
                >
                  Create User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="🔍 Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-2 sm:gap-4 border-b-2 border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 sm:px-6 py-2.5 sm:py-3 font-semibold text-sm sm:text-base whitespace-nowrap transition-all ${
              activeTab === 'all'
                ? 'text-orange-600 border-b-4 border-orange-600 -mb-0.5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 sm:px-6 py-2.5 sm:py-3 font-semibold text-sm sm:text-base whitespace-nowrap transition-all ${
              activeTab === 'pending'
                ? 'text-orange-600 border-b-4 border-orange-600 -mb-0.5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Pending ({pendingUsers.length})
            {pendingUsers.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {pendingUsers.length}
              </span>
            )}
          </button>
        </div>

        {/* Mobile card list */}
        <div className="sm:hidden space-y-3 mb-6">
          {(activeTab === 'all' ? users : pendingUsers).map((u) => {
            const openUserDetails = () => {
              setSelectedUser(u);
              setShowEditDialog(true);
            };
            return (
              <div
                key={u.id}
                data-testid={`user-card-${u.id}`}
                className="bg-white rounded-xl shadow p-3 flex items-center gap-3 active:bg-orange-50"
                onClick={openUserDetails}
                role="button"
                tabIndex={0}
              >
                {u.profile_photo_url ? (
                  <img
                    src={getImageUrl(u.profile_photo_url)}
                    alt={`${u.first_name} ${u.last_name}`}
                    loading="lazy"
                    onClick={(e) => { e.stopPropagation(); previewPhoto(getImageUrl(u.profile_photo_url), `${u.first_name} ${u.last_name}`); }}
                    className="w-14 h-14 rounded-full object-cover border-2 border-orange-300 shrink-0 cursor-zoom-in"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-base font-bold shrink-0">
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {u.first_name} {u.last_name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    {u.is_admin ? (
                      <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0.5">Admin</Badge>
                    ) : u.is_approved ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5">Approved</Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-700 text-[10px] px-1.5 py-0.5">Pending</Badge>
                    )}
                    {!u.is_enabled && (
                      <Badge className="bg-gray-200 text-gray-700 text-[10px] px-1.5 py-0.5">Disabled</Badge>
                    )}
                    <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5">
                      ⭐ {u.rating || 0}
                    </Badge>
                  </div>
                </div>
                <span className="text-gray-400 text-lg shrink-0" aria-hidden>›</span>
              </div>
            );
          })}
          {(activeTab === 'all' ? users : pendingUsers).length === 0 && (
            <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500 text-sm">
              No users found.
            </div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden sm:block bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Rating</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Experience</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(activeTab === 'all' ? users : pendingUsers).map((u) => {
                  const openUserDetails = () => {
                    setSelectedUser(u);
                    setShowEditDialog(true);
                  };
                  return (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="hover:bg-orange-50 cursor-pointer transition-colors"
                    onClick={openUserDetails}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openUserDetails(); } }}
                  >
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-700">{u.user_id || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {u.profile_photo_url ? (
                          <img 
                            src={getImageUrl(u.profile_photo_url)} 
                            alt={`${u.first_name} ${u.last_name}`}
                            loading="lazy"
                            onClick={(e) => { e.stopPropagation(); previewPhoto(getImageUrl(u.profile_photo_url), `${u.first_name} ${u.last_name}`); }}
                            className="w-20 h-20 rounded-full object-cover border-2 border-orange-300 cursor-zoom-in"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-lg font-bold">
                            {u.first_name?.[0]}{u.last_name?.[0]}
                          </div>
                        )}
                        <div>
                          <div className="font-semibold text-gray-900">{u.first_name} {u.last_name}</div>
                          {u.preferred_name && <div className="text-sm text-gray-500">"{u.preferred_name}"</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">{u.email}</td>
                    <td className="px-6 py-4">
                      <Badge className="bg-purple-100 text-purple-700 font-bold">{u.rating || 0}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className="capitalize bg-blue-100 text-blue-700">{u.volleyball_experience}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      {u.is_admin ? (
                        <Badge className="bg-orange-100 text-orange-700">Admin</Badge>
                      ) : u.is_approved ? (
                        <Badge className="bg-green-100 text-green-700">Approved</Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid={`view-user-btn-${u.id}`}
                          onClick={openUserDetails}
                          size="sm"
                          variant="outline"
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          View Details
                        </Button>
                        
                        {!u.is_approved && (
                          <>
                            <Button
                              data-testid={`approve-user-btn-${u.id}`}
                              onClick={() => handleApproveUser(u.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Approve
                            </Button>
                            <Button
                              data-testid={`reject-user-btn-${u.id}`}
                              onClick={() => handleRejectUser(u.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-600 hover:bg-red-50"
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {u.is_approved && (
                          <>
                            <Button
                              data-testid={`toggle-admin-btn-${u.id}`}
                              onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                              size="sm"
                              variant="outline"
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            >
                              {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                            </Button>
                            <Button
                              onClick={() => handleResetPassword(u.id)}
                              size="sm"
                              variant="outline"
                              className="border-purple-500 text-purple-600 hover:bg-purple-50"
                            >
                              Reset Password
                            </Button>
                            {u.is_enabled ? (
                              <Button
                                data-testid={`disable-user-btn-${u.id}`}
                                onClick={() => handleDisableUser(u.id)}
                                size="sm"
                                variant="outline"
                                className="border-gray-500 text-gray-600 hover:bg-gray-50"
                              >
                                Disable
                              </Button>
                            ) : (
                              <Button
                                data-testid={`enable-user-btn-${u.id}`}
                                onClick={() => handleEnableUser(u.id)}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                Enable
                              </Button>
                            )}
                          </>
                        )}
                        {u.id !== user.id && (
                          <Button
                            data-testid={`delete-user-btn-${u.id}`}
                            onClick={() => handleDeleteUser(u.id)}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* User Details Dialog - Editable */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) setIsEditing(false);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold">
              {isEditing ? 'Edit User Details' : 'User Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              {!isEditing ? (
                // View Mode - Table Layout
                <>
                  {/* Profile Photo Section */}
                  <div className="flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left gap-4 sm:gap-6 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg border-2 border-blue-200">
                    <div className="shrink-0">
                      {selectedUser.profile_photo_url ? (
                        <img 
                          src={getImageUrl(selectedUser.profile_photo_url)} 
                          alt={`${selectedUser.first_name} ${selectedUser.last_name}`}
                          onClick={() => previewPhoto(getImageUrl(selectedUser.profile_photo_url), `${selectedUser.first_name} ${selectedUser.last_name}`)}
                          className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white shadow-lg cursor-zoom-in"
                        />
                      ) : (
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-3xl sm:text-4xl font-bold border-4 border-white shadow-lg">
                          {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 break-words">
                        {selectedUser.first_name} {selectedUser.last_name}
                        {selectedUser.preferred_name && <span className="text-base sm:text-lg text-gray-600 ml-2">"{selectedUser.preferred_name}"</span>}
                      </h3>
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3">
                        {selectedUser.is_admin ? (
                          <Badge className="bg-orange-100 text-orange-700 text-sm">Admin</Badge>
                        ) : selectedUser.is_approved ? (
                          <Badge className="bg-green-100 text-green-700 text-sm">Approved</Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700 text-sm">Pending Approval</Badge>
                        )}
                        <div className="flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1.5 rounded-full">
                          <span className="text-sm font-semibold">⭐ Player Rating:</span>
                          <span className="text-base font-bold">{selectedUser.rating || 0}/10</span>
                        </div>
                        <div className="flex items-center gap-2 bg-indigo-100 text-indigo-800 px-3 py-1.5 rounded-full">
                          <span className="text-sm font-semibold">🏐 Community:</span>
                          <span className="text-base font-bold">
                            {selectedUser.community_rating_average ? Number(selectedUser.community_rating_average).toFixed(2) : '—'}/10
                          </span>
                          <span className="text-xs text-indigo-600">({selectedUser.total_games_rated || 0})</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <tbody>
                        <tr className="border-b bg-blue-50">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold text-blue-900 w-1/3">User ID</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-bold text-blue-700">{selectedUser.user_id || 'N/A'}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50 w-1/3">Email</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.email}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Phone</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.phone || 'Not provided'}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Date of Birth</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.date_of_birth || 'Not provided'}</td>
                        </tr>
                        {selectedUser.gender && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Gender</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.gender}</td>
                          </tr>
                        )}
                        <tr className="border-b bg-purple-50">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold text-purple-900">Player Rating</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-bold text-purple-700">{selectedUser.rating || 0} / 10</td>
                        </tr>
                        <tr className="border-b bg-indigo-50">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold text-indigo-900">Community Rating</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-bold text-indigo-700" data-testid="user-community-rating">
                            {selectedUser.community_rating_average ? Number(selectedUser.community_rating_average).toFixed(2) : '—'} / 10
                            <span className="ml-2 text-sm font-normal text-indigo-500">
                              ({selectedUser.total_games_rated || 0} game{selectedUser.total_games_rated === 1 ? '' : 's'} rated)
                            </span>
                          </td>
                        </tr>
                        <tr className="border-b bg-amber-50">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold text-amber-900">Standing</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-bold text-amber-700" data-testid="user-standing">
                            {userStanding?.rank ? (
                              <span className="inline-flex items-center gap-1.5">
                                {`${userStanding.rank_label} / ${userStanding.total}`}
                                <StandingMovement standing={userStanding} />
                              </span>
                            ) : 'Unrated'}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Rating Participation</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4" data-testid="user-rating-participation">
                            <div className="flex items-center gap-2 flex-wrap">
                              {selectedUser.rating_participation_enabled === false ? (
                                <span className="text-red-600 font-semibold">Disabled — rating & kudos hidden from players</span>
                              ) : (
                                <span className="text-green-700 font-semibold">Enabled</span>
                              )}
                              <button
                                onClick={handleToggleParticipation}
                                data-testid="admin-toggle-participation-btn"
                                className={`text-xs font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                                  selectedUser.rating_participation_enabled === false
                                    ? 'border-green-300 text-green-700 hover:bg-green-50'
                                    : 'border-red-300 text-red-600 hover:bg-red-50'
                                }`}
                              >
                                {selectedUser.rating_participation_enabled === false ? 'Enable' : 'Disable'}
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Experience Level</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.volleyball_experience}</td>
                        </tr>
                        {selectedUser.position_1st && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">1st Favorite Position</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.position_1st}</td>
                          </tr>
                        )}
                        {selectedUser.position_2nd && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">2nd Favorite Position</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.position_2nd}</td>
                          </tr>
                        )}
                        {selectedUser.position_3rd && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">3rd Favorite Position</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.position_3rd}</td>
                          </tr>
                        )}
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Club Experience</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.club_experience ? 'Yes' : 'No'}</td>
                        </tr>
                        {(selectedUser.current_club || selectedUser.club_names) && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Current Club</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.current_club || selectedUser.club_names}</td>
                          </tr>
                        )}
                        {selectedUser.previous_clubs && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Previous Clubs</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.previous_clubs}</td>
                          </tr>
                        )}
                        {selectedUser.current_level && selectedUser.volleyball_experience !== 'Beginner' && (
                          <>
                            <tr className="border-b">
                              <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Current Level</td>
                              <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.current_level === 'Other' ? selectedUser.current_level_other : selectedUser.current_level}</td>
                            </tr>
                            <tr className="border-b">
                              <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Highest Level Ever Played</td>
                              <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.highest_level === 'Other' ? selectedUser.highest_level_other : selectedUser.highest_level}</td>
                            </tr>
                          </>
                        )}
                        {selectedUser.where_hear_about_us && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Where They Heard About Us</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.where_hear_about_us}</td>
                          </tr>
                        )}
                        {selectedUser.gdpr_consent !== undefined && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">GDPR Consent</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.gdpr_consent ? '✓ Yes' : '✗ No'}</td>
                          </tr>
                        )}
                        {selectedUser.preferred_positions && selectedUser.preferred_positions.length > 0 && (
                          <tr className="border-b">
                            <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Legacy Preferred Positions</td>
                            <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.preferred_positions.join(', ')}</td>
                          </tr>
                        )}
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Country of Origin</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.country_of_origin || 'Not provided'}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Current Country</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.current_country || 'Not provided'}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Current City</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{selectedUser.current_city || 'Not provided'}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 sm:py-3 sm:px-4 font-semibold bg-gray-50">Member Since</td>
                          <td className="py-2 px-3 sm:py-3 sm:px-4">{new Date(selectedUser.created_at).toLocaleDateString()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {/* Admin Actions */}
                  <div className="mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Admin Actions</div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={handleEditClick}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`edit-user-action-${selectedUser.id}`}
                      >
                        ✏️ Edit User
                      </Button>

                      {!selectedUser.is_approved && (
                        <>
                          <Button
                            onClick={async () => { await handleApproveUser(selectedUser.id); setShowEditDialog(false); }}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`approve-user-action-${selectedUser.id}`}
                          >
                            ✓ Approve
                          </Button>
                          <Button
                            onClick={async () => { await handleRejectUser(selectedUser.id); setShowEditDialog(false); }}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-600 hover:bg-red-50"
                            data-testid={`reject-user-action-${selectedUser.id}`}
                          >
                            ✕ Reject
                          </Button>
                        </>
                      )}

                      {selectedUser.is_approved && (
                        <>
                          <Button
                            onClick={() => handleToggleAdmin(selectedUser.id, selectedUser.is_admin)}
                            size="sm"
                            variant="outline"
                            className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            data-testid={`toggle-admin-action-${selectedUser.id}`}
                          >
                            {selectedUser.is_admin ? 'Remove Admin' : 'Make Admin'}
                          </Button>
                          <Button
                            onClick={() => handleResetPassword(selectedUser.id)}
                            size="sm"
                            variant="outline"
                            className="border-purple-500 text-purple-600 hover:bg-purple-50"
                          >
                            🔑 Reset Password
                          </Button>
                          {selectedUser.is_enabled ? (
                            <Button
                              onClick={async () => { await handleDisableUser(selectedUser.id); setShowEditDialog(false); }}
                              size="sm"
                              variant="outline"
                              className="border-gray-500 text-gray-600 hover:bg-gray-50"
                              data-testid={`disable-user-action-${selectedUser.id}`}
                            >
                              🚫 Disable
                            </Button>
                          ) : (
                            <Button
                              onClick={async () => { await handleEnableUser(selectedUser.id); setShowEditDialog(false); }}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`enable-user-action-${selectedUser.id}`}
                            >
                              ✓ Enable
                            </Button>
                          )}
                        </>
                      )}

                      {selectedUser.id !== user.id && (
                        <Button
                          onClick={async () => { await handleDeleteUser(selectedUser.id); setShowEditDialog(false); }}
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-600 hover:bg-red-50"
                          data-testid={`delete-user-action-${selectedUser.id}`}
                        >
                          🗑️ Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button onClick={() => setShowEditDialog(false)} variant="outline" className="flex-1 sm:flex-none">
                      Close
                    </Button>
                  </div>
                </>
              ) : (
                // Edit Mode
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit_first_name">First Name</Label>
                      <Input
                        id="edit_first_name"
                        value={editFormData.first_name}
                        onChange={(e) => setEditFormData({...editFormData, first_name: e.target.value})}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_last_name">Last Name</Label>
                      <Input
                        id="edit_last_name"
                        value={editFormData.last_name}
                        onChange={(e) => setEditFormData({...editFormData, last_name: e.target.value})}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit_preferred_name">Preferred Name</Label>
                    <Input
                      id="edit_preferred_name"
                      value={editFormData.preferred_name || ''}
                      onChange={(e) => setEditFormData({...editFormData, preferred_name: e.target.value})}
                      className="mt-2"
                      placeholder="If different from first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_email">Email</Label>
                    <Input
                      id="edit_email"
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_phone">Phone</Label>
                    <Input
                      id="edit_phone"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_dob">Date of Birth</Label>
                    <Input
                      id="edit_dob"
                      type="date"
                      value={editFormData.date_of_birth}
                      onChange={(e) => setEditFormData({...editFormData, date_of_birth: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_profile_photo">Profile Photo (Optional)</Label>
                    {selectedUser?.profile_photo_url && !editPhotoPreview && (
                      <div className="mt-2 mb-2">
                        <img src={getImageUrl(selectedUser.profile_photo_url)} alt="Current photo" className="w-20 h-20 rounded-full object-cover border-2 border-gray-300" />
                        <p className="text-xs text-gray-500 mt-1">Current photo</p>
                      </div>
                    )}
                    <Input
                      id="edit_profile_photo"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('File size must be less than 5MB');
                            e.target.value = '';
                            return;
                          }
                          setEditPhoto(file);
                          setEditPhotoPreview(URL.createObjectURL(file));
                        }
                      }}
                      className="mt-2"
                    />
                    {editPhotoPreview && (
                      <div className="mt-3 flex items-center gap-4">
                        <img src={editPhotoPreview} alt="New preview" className="w-20 h-20 rounded-full object-cover border-2 border-orange-500" />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditPhoto(null);
                            setEditPhotoPreview(null);
                            document.getElementById('edit_profile_photo').value = '';
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Upload new photo (jpg, png, webp - max 5MB)</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_experience">Volleyball Experience</Label>
                    <Select
                      value={editFormData.volleyball_experience}
                      onValueChange={(value) => setEditFormData({...editFormData, volleyball_experience: value})}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {experienceLevels.map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Club Experience</Label>
                    <div className="flex gap-4 mt-2">
                      <div className="flex items-center space-x-2">
                        <NativeCheckbox
                          checked={editFormData.club_experience === true}
                          onChange={(checked) => setEditFormData({...editFormData, club_experience: true})}
                        />
                        <span className="text-sm">Yes</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <NativeCheckbox
                          checked={editFormData.club_experience === false}
                          onChange={(checked) => setEditFormData({...editFormData, club_experience: false})}
                        />
                        <span className="text-sm">No</span>
                      </div>
                    </div>
                  </div>
                  {editFormData.club_experience && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="edit_current_club">Current Club</Label>
                        <Input
                          id="edit_current_club"
                          value={editFormData.current_club}
                          onChange={(e) => setEditFormData({...editFormData, current_club: e.target.value})}
                          className="mt-2"
                          placeholder="e.g., Amsterdam Volleyball Club"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_previous_clubs">Previous Clubs (optional)</Label>
                        <Input
                          id="edit_previous_clubs"
                          value={editFormData.previous_clubs}
                          onChange={(e) => setEditFormData({...editFormData, previous_clubs: e.target.value})}
                          className="mt-2"
                          placeholder="e.g., Rotterdam VC, Utrecht Smash — separate with commas"
                        />
                      </div>
                    </div>
                  )}

                  {/* NEW ENHANCED FIELDS */}
                  <div>
                    <Label htmlFor="edit_gender">Gender</Label>
                    <Select
                      value={editFormData.gender}
                      onValueChange={(value) => setEditFormData({...editFormData, gender: value})}
                    >
                      <SelectTrigger className="mt-2" id="edit_gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {genderOptions.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="edit_rating">Player Rating (0-10)</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        id="edit_rating"
                        type="number"
                        min="0"
                        max="10"
                        step="0.5"
                        value={editFormData.rating}
                        onChange={(e) => setEditFormData({...editFormData, rating: parseFloat(e.target.value) || 0})}
                        className="flex-1"
                        data-testid="edit-rating-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditFormData({...editFormData, rating: 0})}
                        disabled={!editFormData.rating}
                        data-testid="remove-admin-rating-btn"
                        className="whitespace-nowrap"
                      >
                        Remove rating
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Admin skill rating. Set to <span className="font-semibold">0</span> (or click "Remove rating") to clear the admin rating — the player's community rating will be used instead.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="edit_position_1st">1st Favorite Position</Label>
                    <Select
                      value={editFormData.position_1st}
                      onValueChange={(value) => setEditFormData({...editFormData, position_1st: value})}
                    >
                      <SelectTrigger className="mt-2" id="edit_position_1st">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="edit_position_2nd">2nd Position (Optional)</Label>
                    <Select
                      value={editFormData.position_2nd}
                      onValueChange={(value) => setEditFormData({...editFormData, position_2nd: value})}
                    >
                      <SelectTrigger className="mt-2" id="edit_position_2nd">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="edit_position_3rd">3rd Position (Optional)</Label>
                    <Select
                      value={editFormData.position_3rd}
                      onValueChange={(value) => setEditFormData({...editFormData, position_3rd: value})}
                    >
                      <SelectTrigger className="mt-2" id="edit_position_3rd">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Conditional Level Fields */}
                  {editFormData.volleyball_experience && editFormData.volleyball_experience !== 'Beginner' && (
                    <>
                      <div>
                        <Label htmlFor="edit_current_level">Current Level</Label>
                        <Select
                          value={editFormData.current_level}
                          onValueChange={(value) => setEditFormData({...editFormData, current_level: value})}
                        >
                          <SelectTrigger className="mt-2" id="edit_current_level">
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            {levels.map((level) => (
                              <SelectItem key={level} value={level}>{level}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {editFormData.current_level === 'Other' && (
                        <div>
                          <Label htmlFor="edit_current_level_other">Specify Current Level *</Label>
                          <Input
                            id="edit_current_level_other"
                            value={editFormData.current_level_other}
                            onChange={(e) => setEditFormData({...editFormData, current_level_other: e.target.value})}
                            className="mt-2"
                          />
                        </div>
                      )}

                      <div>
                        <Label htmlFor="edit_highest_level">Highest Level Ever Played</Label>
                        <Select
                          value={editFormData.highest_level}
                          onValueChange={(value) => setEditFormData({...editFormData, highest_level: value})}
                        >
                          <SelectTrigger className="mt-2" id="edit_highest_level">
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            {levels.map((level) => (
                              <SelectItem key={level} value={level}>{level}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {editFormData.highest_level === 'Other' && (
                        <div>
                          <Label htmlFor="edit_highest_level_other">Specify Highest Level *</Label>
                          <Input
                            id="edit_highest_level_other"
                            value={editFormData.highest_level_other}
                            onChange={(e) => setEditFormData({...editFormData, highest_level_other: e.target.value})}
                            className="mt-2"
                          />
                        </div>
                      )}
                    </>
                  )}

                  <div>
                    <Label htmlFor="edit_where_hear">Where Did You Hear About Us?</Label>
                    <Input
                      id="edit_where_hear"
                      value={editFormData.where_hear_about_us}
                      onChange={(e) => setEditFormData({...editFormData, where_hear_about_us: e.target.value})}
                      className="mt-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit_country_origin">Country of Origin</Label>
                      <Select
                        value={editFormData.country_of_origin}
                        onValueChange={(value) => setEditFormData({...editFormData, country_of_origin: value})}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit_current_country">Current Country</Label>
                      <Select
                        value={editFormData.current_country}
                        onValueChange={(value) => setEditFormData({...editFormData, current_country: value})}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit_city">Current City</Label>
                    <Input
                      id="edit_city"
                      value={editFormData.current_city}
                      onChange={(e) => setEditFormData({...editFormData, current_city: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <NativeCheckbox
                        checked={editFormData.is_approved}
                        onChange={(checked) => setEditFormData({...editFormData, is_approved: checked})}
                      />
                      <Label>Approved User</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <NativeCheckbox
                        checked={editFormData.is_admin}
                        onChange={(checked) => setEditFormData({...editFormData, is_admin: checked})}
                      />
                      <Label>Admin User</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <NativeCheckbox
                        checked={editFormData.is_enabled}
                        onChange={(checked) => setEditFormData({...editFormData, is_enabled: checked})}
                      />
                      <Label>Enabled</Label>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-6 sticky bottom-0 bg-white py-2">
                    <Button onClick={handleSaveUser} className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none">
                      Save Changes
                    </Button>
                    <Button onClick={() => setIsEditing(false)} variant="outline" className="flex-1 sm:flex-none">
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
